import { useQuery } from '@apollo/client/react';
import { Box, Stack, Typography } from '@mui/material';
import { Eye } from 'lucide-react';

import { useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';
import { getTenantHostname } from '@shared/utils/tenant';

import { CLAIM_PROFILE_IMPRESSIONS } from './claimQueries';

// Below this the count reads as weak rather than reassuring: hide it.
const MIN_IMPRESSIONS = 64;
// Impressions are tracked from mid-2026: any earlier start date means "all time".
const ALL_TIME_START = '2026-01-01';

type Props = {
  isAgency: boolean;
  teamId: string | null;
  userId: string;
  tenantId: string | null | undefined;
};

export const ProfileImpressionsChip = ({
  isAgency,
  teamId,
  userId,
  tenantId,
}: Props) => {
  const { t, locale } = useLocale();
  const hostname = tenantId != null ? getTenantHostname(tenantId) : null;

  const { data } = useQuery(CLAIM_PROFILE_IMPRESSIONS, {
    variables: {
      teamId: teamId ?? '',
      userId: isAgency ? null : userId,
      hostname: hostname ?? '',
      startDate: ALL_TIME_START,
      endDate: new Date().toISOString().slice(0, 10),
      eventNames: [isAgency ? 'agency_impression' : 'agent_impression'],
    },
    skip: teamId == null || hostname == null || (!isAgency && userId === ''),
  });

  const count = data?.impressions_total?.count ?? 0;
  if (count < MIN_IMPRESSIONS) {
    return null;
  }

  const views = new Intl.NumberFormat(locale).format(count);

  return (
    <Stack
      direction="row"
      spacing={1.25}
      sx={{
        alignItems: 'center',
        pl: 0.75,
        pr: 1.75,
        py: 0.75,
        borderRadius: 999,
        bgcolor: 'background.paper',
        border: '1px solid rgba(82,120,223,0.18)',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        textAlign: 'left',
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: 999,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: customPalette.blue50,
          color: 'primary.main',
        }}
      >
        <Eye size={15} />
      </Box>
      <Typography sx={{ fontSize: 14, color: 'text.primary' }}>
        {isAgency
          ? t('Your agency was shown {{views}} times on RealAdvisor', {
              views,
            })
          : t('Your profile was shown {{views}} times on RealAdvisor', {
              views,
            })}
      </Typography>
    </Stack>
  );
};
